import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Select, Input, Spin, DatePicker, Checkbox } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useDispatch, useSelector } from "react-redux";
import { Note } from "@phosphor-icons/react";
import {
  createInboxTaskThunk,
  selectInboxMutationsForDocument,
} from "../../slice/InboxSlice";
import { normalizeShareRecipients } from "../../utils/shareRecipients";
import { getShareRecipients } from "../../slice/MatterDetailsSlice";
import {
  InboxModalErrorBanner,
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
} from "../AIPane/AIPaneStyled";
import BesideDocumentDrawer from "./besideDocumentDrawer";

/**
 * Create Task. One form from a document, a matter, or Home with neither
 * (Matthew, 2026-08-18). Due date and urgent are always available.
 * Opens as a right-hand panel so an open document stays readable.
 * Switching letters while it is open keeps the note.
 *
 * Recipients come from the existing share-users list: scoped to a
 * document when there is one, otherwise every active teammate.
 */

dayjs.extend(utc);

/**
 * Turn a picked calendar day into the instant to send as `due_date`.
 *
 * End of day, so "due the 3rd" is not already overdue at one minute past
 * midnight on the 3rd. End of day in **UTC**, not local, because the
 * Assignments tab renders `due_date` with `dayjs.utc` -- a local
 * end-of-day from anywhere west of Greenwich lands in the next UTC day
 * and the tab would show the user a date one later than the one they
 * picked. Only the calendar date the user chose is carried over; their
 * clock time is deliberately discarded.
 */
export const dueDateToUtcEndOfDay = (picked) =>
  picked ? dayjs.utc(picked.format("YYYY-MM-DD")).endOf("day").toISOString() : null;

/**
 * Whether a calendar day cannot be chosen as a due date.
 *
 * Derived from `dueDateToUtcEndOfDay` rather than comparing local days,
 * so the picker and the value it produces cannot disagree. Comparing
 * local days instead would let an Americas user pick "today" in the
 * evening, once UTC has rolled past it, and get an assignment that is
 * already overdue the moment it is created -- the outcome the
 * end-of-day handling exists to prevent.
 *
 * A consequence worth naming: after roughly 6pm Mountain, the earliest
 * selectable day is tomorrow. In a deadline system anchored to UTC that
 * is honest -- today has genuinely run out -- and it is better than
 * accepting the choice and filing it straight under Overdue.
 *
 * `now` is injectable so the boundary can be tested without freezing
 * the clock for everything else in the file.
 */
export const isDueDateUnselectable = (current, now = dayjs()) => {
  const iso = dueDateToUtcEndOfDay(current);
  return Boolean(iso) && dayjs(iso).isBefore(now);
};

const AssignTaskModal = ({
  open,
  documentId,
  matterId,
  onClose,
  onCreated,
}) => {
  const dispatch = useDispatch();
  const [assignedTo, setAssignedTo] = useState(null);
  const [body, setBody] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [urgent, setUrgent] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientsStatus, setRecipientsStatus] = useState("idle");
  const [submitError, setSubmitError] = useState(null);
  const [recipientsFor, setRecipientsFor] = useState(null);

  const mutations = useSelector((state) =>
    selectInboxMutationsForDocument(state, documentId)
  );
  const submitting = mutations.task === "loading";
  const recipientsAreCurrent =
    documentId == null
      ? recipientsStatus === "ready"
      : recipientsFor != null && String(recipientsFor) === String(documentId);

  // Wipe the draft only when the panel is closed. Switching mail must
  // not throw away the note (Matthew, 2026-09-14).
  useEffect(() => {
    if (open) return;
    setAssignedTo(null);
    setBody("");
    setDueDate(null);
    setUrgent(false);
    setSubmitError(null);
    setRecipientsFor(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRecipientsStatus((status) =>
      status === "ready" ? "ready" : "loading"
    );
    dispatch(getShareRecipients({ docId: documentId, reviewersOnly: false }))
      .unwrap()
      .then((response) => {
        if (cancelled) return;
        const next = normalizeShareRecipients(response);
        setRecipients(next);
        setRecipientsStatus("ready");
        setRecipientsFor(documentId ?? "none");
        const ids = new Set(next.filter((r) => r?.id != null).map((r) => r.id));
        setAssignedTo((current) => (ids.has(current) ? current : null));
      })
      .catch((err) => {
        if (cancelled) return;
        setRecipientsStatus("failed");
        setSubmitError(
          err?.message || "Couldn't load the list of teammates to assign to."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, documentId, dispatch]);

  const recipientOptions = useMemo(
    () =>
      recipients
        .filter((r) => r?.id != null)
        .map((r) => ({
          value: r.id,
          label: r.full_name || r.username || r.email || `User #${r.id}`,
        })),
    [recipients]
  );

  const trimmedBody = body.trim();
  const canSubmit =
    assignedTo != null &&
    trimmedBody.length > 0 &&
    !submitting &&
    recipientsAreCurrent;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    setSubmitError(null);
    dispatch(
      createInboxTaskThunk({
        documentId: documentId || undefined,
        matterId: matterId || undefined,
        assignedToUserId: assignedTo,
        body: trimmedBody,
        dueDate: dueDateToUtcEndOfDay(dueDate),
        priority: urgent ? "urgent" : undefined,
      })
    )
      .unwrap()
      .then(() => {
        if (typeof onCreated === "function") onCreated();
        setBody("");
        setDueDate(null);
        setUrgent(false);
        setSubmitError(null);
      })
      .catch((err) => {
        setSubmitError(
          err?.message || "Couldn't create the Task. Please try again."
        );
      });
  }, [
    canSubmit,
    documentId,
    matterId,
    dispatch,
    assignedTo,
    trimmedBody,
    onCreated,
    dueDate,
    urgent,
  ]);

  return (
    <BesideDocumentDrawer
      open={open}
      onClose={onClose}
      onOk={handleSubmit}
      okText="Assign"
      okDisabled={!canSubmit}
      okLoading={submitting}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Note size={16} weight="bold" />
          Create Task
        </span>
      }
    >
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="inbox-task-assignee">Assign to</InboxModalLabel>
        <Select
          id="inbox-task-assignee"
          style={{ width: "100%" }}
          placeholder={
            recipientsStatus === "loading" ? "Loading…" : "Choose a teammate…"
          }
          options={recipientOptions}
          value={assignedTo}
          onChange={setAssignedTo}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          notFoundContent={
            recipientsStatus === "loading" ? (
              <Spin size="small" />
            ) : (
              "No matching teammates"
            )
          }
          disabled={recipientsStatus === "loading" && recipients.length === 0}
        />
      </InboxModalFieldGroup>

      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="inbox-task-body">
          What needs doing?
        </InboxModalLabel>
        <Input.TextArea
          id="inbox-task-body"
          autoSize={{ minRows: 3, maxRows: 8 }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="e.g. Draft the appellant brief; lead with the CUE argument."
          maxLength={2000}
          showCount
        />
        <InboxModalHint>
          {documentId
            ? "The assignee gets a notification with a link back to the letter you're looking at."
            : "The assignee gets a notification. You can send the work product for review later."}
        </InboxModalHint>
      </InboxModalFieldGroup>

      <InboxModalFieldGroup>
        <Checkbox
          id="inbox-task-urgent"
          checked={urgent}
          onChange={(e) => setUrgent(e.target.checked)}
        >
          Mark as urgent
        </Checkbox>
        <InboxModalHint>
          {urgent
            ? "The assignee will see this in a banner they have to dismiss, "
              + "not just the notification bell."
            : "Only check this for work that needs immediate attention -- "
              + "it interrupts the assignee with a banner."}
        </InboxModalHint>
      </InboxModalFieldGroup>

      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="inbox-task-due">
          Due date (optional)
        </InboxModalLabel>
        <DatePicker
          id="inbox-task-due"
          style={{ width: "100%" }}
          value={dueDate}
          onChange={setDueDate}
          format="DD MMM YYYY"
          placeholder="No due date"
          disabledDate={(current) => isDueDateUnselectable(current)}
        />
      </InboxModalFieldGroup>

      {submitError && (
        <InboxModalErrorBanner role="alert">{submitError}</InboxModalErrorBanner>
      )}
    </BesideDocumentDrawer>
  );
};

AssignTaskModal.propTypes = {
  open: PropTypes.bool.isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};

export default AssignTaskModal;
